import type { FC } from 'react';
import React, { useCallback } from 'react';
import { Container } from '@mui/material';
import type { UrlSort } from '@pflegenavi/web-components';
import {
  HeaderBreadcrumbs,
  Stack,
  urlString,
  useUrlFilters,
  useUrlTableState,
} from '@pflegenavi/web-components';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useTranslation } from 'react-i18next';
import { useInitializeResidents } from '../hooks/useUnitializeResidents';
import { useGetPayoutsPhoenix } from '@pflegenavi/service-provider-frontend/api';
import { PayoutsTable } from '../components/payouts/PayoutsTable';
import { PayoutTabValues } from '../components/payouts/types';
import type { PayoutSortColumn } from '../components/payouts/types';
import { OpenItemsDownload } from '../components/payouts/OpenItemsDownload';

const SORT_COLUMNS: readonly PayoutSortColumn[] = ['inserted_at'];
const DEFAULT_SORT: UrlSort<PayoutSortColumn> = {
  column: 'inserted_at',
  direction: 'desc',
};
// The tab lives in the URL so the view survives navigating away and back.
const FILTER_CODECS = { state: urlString('state') };

const MANGOPAY_STATE: Record<
  PayoutTabValues,
  'CREATED' | 'SUCCEEDED' | 'FAILED' | undefined
> = {
  [PayoutTabValues.All]: undefined,
  [PayoutTabValues.Paid]: 'SUCCEEDED',
  [PayoutTabValues.Pending]: 'CREATED',
  [PayoutTabValues.Failed]: 'FAILED',
};

export const PayoutsPage: FC = () => {
  const { t } = useTranslation();

  useInitializeResidents();

  const { page, pageSize, sort, setPage, setPageSize, setSort } =
    useUrlTableState({
      defaultPageSize: 5,
      sortColumns: SORT_COLUMNS,
      defaultSort: DEFAULT_SORT,
    });
  const { values, set } = useUrlFilters(FILTER_CODECS);
  // An unknown value falls back to All, so a hand-edited URL cannot fail the request.
  const tab =
    Object.values(PayoutTabValues).find((value) => value === values.state) ??
    PayoutTabValues.All;
  // Stable, as the table config depends on it.
  const onTabChange = useCallback(
    (value: PayoutTabValues) =>
      set('state', value === PayoutTabValues.All ? undefined : value),
    [set]
  );

  const {
    data: payouts,
    isLoading,
    isError,
  } = useGetPayoutsPhoenix({
    params: {
      page,
      pageSize,
      state: MANGOPAY_STATE[tab],
      orderBy: (sort ?? DEFAULT_SORT).column,
      orderDirection: (sort ?? DEFAULT_SORT).direction,
    },
  });

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('dashboard.main')}
        links={[
          {
            name: t('dashboard.main'),
            href: spAppMainPages.DASHBOARD,
          },
          { name: t('payouts.main') },
        ]}
        action={<OpenItemsDownload />}
      />
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <PayoutsTable
          payouts={payouts}
          isLoading={isLoading}
          isError={isError}
          totalCount={payouts?.meta.total_count ?? 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          tab={tab}
          onTabChange={onTabChange}
          sort={sort ?? DEFAULT_SORT}
          onSortChange={setSort}
        />
      </Stack>
    </Container>
  );
};
